/**
 * LayoutsCortados — telas do Design Studio cujo layout foi gravado pela metade.
 *
 * Defeito visto em campo (Picky V10, 2026-10-06): 9 de 82 layouts foram gravados cortados —
 * a resposta da IA bateu no teto no meio do HTML — e seguiram para o codegen como referência
 * visual da tela. Agora a API devolve `layout_cortado` (o motivo) em cada tela; aqui o dono
 * vê quais são e conserta com um clique: só as cortadas são refeitas, no mesmo lugar da lista.
 * Tela cortada NÃO vai ao codegen como referência.
 *
 * a11y: região role="alert"; o botão diz quantas telas vai refazer.
 */
import { C } from '@/theme/palette';

/** Telas com layout cortado: `[{ id, name, layout_cortado }]`. */
export function telasComLayoutCortado(screens) {
  return (screens || []).filter((s) => s && s.layout_cortado);
}

/**
 * @param {{ screens: object[], onConsertar: () => void, consertando?: boolean,
 *           erro?: string|null }} props
 */
export function LayoutsCortados({ screens, onConsertar, consertando = false, erro = null }) {
  const cortadas = telasComLayoutCortado(screens);
  if (!cortadas.length) return null;
  const n = cortadas.length.toLocaleString('pt-BR');
  const total = (screens || []).length.toLocaleString('pt-BR');
  return (
    <div role="alert" data-testid="layouts-cortados" style={{ margin: '10px 0 0', padding: '10px 12px',
      borderRadius: 8, border: `1px solid ${C.warn}66`, fontSize: 12.5, lineHeight: 1.5 }}>
      <div style={{ color: C.warn, fontWeight: 600 }}>
        {n} de {total} tela(s) estão com o layout cortado
      </div>
      <div style={{ color: C.dim, marginTop: 2 }}>
        A IA parou no meio do HTML destas telas. Elas não vão ao codegen como referência visual
        enquanto não forem refeitas:
      </div>
      <ul style={{ margin: '6px 0 8px', paddingLeft: 18, color: C.text }}>
        {cortadas.map((s) => (
          <li key={s.id || s.name}>
            {s.name} <span style={{ color: C.muted }}>— {s.layout_cortado}</span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onConsertar}
        disabled={consertando}
        aria-label={`Consertar o layout de ${n} tela(s)`}
        style={{ cursor: consertando ? 'default' : 'pointer', background: 'transparent',
          color: C.warn, border: `1px solid ${C.warn}`, borderRadius: 8, padding: '7px 13px',
          fontSize: 12.5, fontWeight: 600, opacity: consertando ? 0.6 : 1 }}>
        {consertando ? 'Refazendo as telas cortadas…' : '🛠 Consertar layouts'}
      </button>
      {erro && (
        <div style={{ marginTop: 8, color: C.digital }}>Não foi possível consertar: {String(erro)}</div>
      )}
    </div>
  );
}
